import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { hash } from '../engine/noise';
import type { Materials } from '../scene/materials';

/** Body part tag written into the `aPart` vertex attribute (used by the walk shader). */
export const enum Part { Body = 0, LegL = 1, LegR = -1, ShinL = 2, ShinR = -2, FootL = 3, FootR = -3, ArmL = 4, ArmR = -4, ForearmL = 5, ForearmR = -5, Cloth = 6, Head = 7 }

export interface WarriorTemplate {
  /** One merged geometry per material, already carrying the `aPart` attribute. */
  parts: { mat: THREE.Material; geo: THREE.BufferGeometry; cast: boolean }[];
  /** Local positions of the visor and the chest core (for glows / hit points). */
  eye: THREE.Vector3; core: THREE.Vector3;
  /** Height of the model at scale 1. */
  height: number;
}

type Sink = (geo: THREE.BufferGeometry, mat: THREE.Material, m: THREE.Matrix4, part: Part, cast?: boolean) => void;

const _o = new THREE.Object3D();
/** Geometric detail of the current build (quality tier): segment counts scale with it. */
let D = 1;
const seg = (n: number, min = 4) => Math.max(min, Math.round(n * D));
function P(sink: Sink, base: THREE.Matrix4, part: Part, geo: THREE.BufferGeometry, mat: THREE.Material, px: number, py: number, pz: number, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1, cast = true) {
  _o.position.set(px, py, pz); _o.rotation.set(rx, ry, rz); _o.scale.set(sx, sy, sz); _o.updateMatrix();
  sink(geo, mat, base.clone().multiply(_o.matrix), part, cast);
}

/** The armoured soldier from the mock, emitted as tagged primitives. */
function soldier(sink: Sink, M: Materials, seed: number, boss: 'brute' | 'herald' | null) {
  const r = (i: number) => hash(i, seed, 77);
  const A = boss ? M.bossArmor : M.armor, Tr = boss ? M.bossTrim : M.trim;
  const cloth = boss ? M.bossCloth : r(60) > 0.5 ? M.cloth : M.clothDark;
  const I = new THREE.Matrix4();
  ([-1, 1] as const).forEach((sd) => {
    // legs are modelled around their hip pivot (y = .56); the shader swings them
    const leg = new THREE.Matrix4().makeTranslation(sd * 0.1, 0.56, 0), part = sd < 0 ? Part.LegL : Part.LegR;
    P(sink, leg, part, new THREE.CylinderGeometry(0.08, 0.066, 0.28, seg(10)), A, 0, -0.14, 0);
    const shin = sd < 0 ? Part.ShinL : Part.ShinR, foot = sd < 0 ? Part.FootL : Part.FootR;
    P(sink, leg, shin, new THREE.SphereGeometry(0.074, seg(10), seg(8, 3)), Tr, 0, -0.29, 0.035);
    P(sink, leg, shin, new THREE.ConeGeometry(0.03, 0.1, seg(5, Math.min(5, 4))), Tr, 0, -0.29, 0.1, Math.PI / 2);
    P(sink, leg, shin, new THREE.CylinderGeometry(0.066, 0.052, 0.26, seg(10)), A, 0, -0.42, 0);
    P(sink, leg, foot, new THREE.BoxGeometry(0.12, 0.07, 0.22), A, 0, -0.53, 0.045);
  });
  const B = Part.Body;
  P(sink, I, B, new THREE.CylinderGeometry(0.19, 0.26, 0.2, seg(8)), A, 0, 0.6, 0);
  const prof = [[0, 0], [0.18, 0], [0.21, 0.08], [0.26, 0.21], [0.28, 0.31], [0.21, 0.4], [0.1, 0.44], [0, 0.44]].map(([x, y]) => new THREE.Vector2(x, y));
  P(sink, I, B, new THREE.LatheGeometry(prof, seg(18, 8)), A, 0, 0.66, 0, 0, 0, 0, 1, 1, 0.8);
  P(sink, I, B, new THREE.BoxGeometry(0.035, 0.3, 0.05), Tr, 0, 0.87, 0.205, -0.2);
  P(sink, I, B, new THREE.SphereGeometry(0.04, seg(12), seg(8, 3)), M.eye, 0, 0.83, 0.22, 0, 0, 0, 1, 1, 1, false);
  P(sink, I, B, new THREE.CylinderGeometry(0.1, 0.15, 0.09, seg(10)), Tr, 0, 1.1, 0);
  ([-1, 1] as const).forEach((sd) => {
    const arm = sd < 0 ? Part.ArmL : Part.ArmR;
    const forearm = sd < 0 ? Part.ForearmL : Part.ForearmR;
    P(sink, I, B, new THREE.SphereGeometry(0.14, seg(14), seg(10, 3), 0, Math.PI * 2, 0, Math.PI / 2), A, sd * 0.28, 1.0, 0, 0, 0, sd * -0.35, 1.2, 0.95, 1.1);
    P(sink, I, B, new THREE.SphereGeometry(0.14, seg(14), seg(10, 3), 0, Math.PI * 2, 0, Math.PI / 2), Tr, sd * 0.31, 0.94, 0, 0, 0, sd * -0.55, 1.05, 0.7, 1);
    const ns = 2 + Math.floor(r(sd + 5) * 2);
    for (let i = 0; i < ns; i++) P(sink, I, B, new THREE.ConeGeometry(0.03, 0.22 + r(i + sd * 7) * 0.14, seg(6)), Tr, sd * (0.28 + i * 0.06), 1.12 + i * 0.01, -0.04 + i * 0.05, -0.25, 0, -sd * (0.5 + i * 0.25));
    P(sink, I, arm, new THREE.CylinderGeometry(0.062, 0.056, 0.21, seg(8)), A, sd * 0.32, 0.855, 0, 0, 0, sd * 0.08);
    P(sink, I, forearm, new THREE.CylinderGeometry(0.056, 0.052, 0.22, seg(8)), A, sd * 0.34, 0.64, 0.02, 0, 0, sd * 0.08);
    P(sink, I, forearm, new THREE.CylinderGeometry(0.074, 0.062, 0.15, seg(8)), Tr, sd * 0.35, 0.55, 0.02, 0, 0, sd * 0.08);
  });
  P(sink, I, Part.Head, new THREE.CylinderGeometry(0.115, 0.1, 0.17, seg(14)), A, 0, 1.21, 0);
  P(sink, I, Part.Head, new THREE.SphereGeometry(0.115, seg(16), seg(10, 3), 0, Math.PI * 2, 0, Math.PI / 2), A, 0, 1.295, 0);
  P(sink, I, Part.Head, new THREE.BoxGeometry(0.17, 0.13, 0.06), Tr, 0, 1.2, 0.08);
  P(sink, I, Part.Head, new THREE.BoxGeometry(0.14, 0.022, 0.02), M.eye, 0, 1.225, 0.115, 0, 0, 0, 1, 1, 1, false);
  const nc = !boss && seed === 23 ? 1 : 3 + Math.floor(r(9) * 3);
  for (let i = 0; i < nc; i++) { const a = (i - (nc - 1) / 2) * 0.38; P(sink, I, Part.Head, new THREE.ConeGeometry(0.024, 0.22 + r(20 + i) * 0.2, seg(6)), Tr, Math.sin(a) * 0.08, 1.38, -0.03, -0.4, 0, -a * 1.2); }
  if (!boss) {
    if (seed === 23) {
      P(sink, I, Part.ForearmR, new THREE.CylinderGeometry(0.015, 0.015, 1.75, seg(6)), M.leather, 0.38, 0.86, 0.12, 0.1);
      const bl = new THREE.Shape(); bl.moveTo(0, 0); bl.lineTo(0.22, 0.05); bl.quadraticCurveTo(0.27, 0.2, 0.17, 0.36); bl.lineTo(0, 0.25); bl.closePath();
      P(sink, I, Part.ForearmR, new THREE.ExtrudeGeometry(bl, { depth: 0.014, bevelEnabled: false }), Tr, 0.38, 1.46, 0.19, 0.1, Math.PI / 2, 0);
      P(sink, I, Part.ForearmR, new THREE.ConeGeometry(0.026, 0.26, seg(4, Math.min(4, 4))), Tr, 0.38, 1.84, 0.22, 0.1);
    } else if (seed === 11) {
      P(sink, I, Part.ForearmR, new THREE.BoxGeometry(0.055, 0.95, 0.014), Tr, 0.4, 0.92, 0.18, 0.25, 0, -0.05);
      P(sink, I, Part.ForearmR, new THREE.BoxGeometry(0.22, 0.035, 0.045), Tr, 0.4, 0.47, 0.1, 0.25, 0, -0.05);
      const sh = new THREE.Shape(); sh.moveTo(-0.18, 0.24); sh.lineTo(0.18, 0.24); sh.lineTo(0.16, -0.05); sh.lineTo(0, -0.32); sh.lineTo(-0.16, -0.05); sh.closePath();
      P(sink, I, Part.ForearmL, new THREE.ExtrudeGeometry(sh, { depth: 0.03, bevelEnabled: true, bevelThickness: 0.014, bevelSize: 0.014, bevelSegments: 1 }), A, -0.37, 0.74, 0.1, 0, -0.3, 0);
      P(sink, I, Part.ForearmL, new THREE.BoxGeometry(0.025, 0.36, 0.025), Tr, -0.37, 0.73, 0.145, 0, -0.3);
    } else {
      // Raider: a low, broad axe and a ragged crest instead of the long polearm/shield.
      P(sink, I, Part.ForearmR, new THREE.CylinderGeometry(0.023, 0.023, 0.95, seg(6)), M.leather, 0.38, 0.74, 0.14, 0.2);
      P(sink, I, Part.ForearmR, new THREE.BoxGeometry(0.32, 0.23, 0.045), Tr, 0.42, 1.16, 0.22, 0.2, 0.15);
      P(sink, I, Part.ForearmR, new THREE.BoxGeometry(0.08, 0.27, 0.05), A, 0.27, 1.16, 0.22, 0.2, 0.15);
      P(sink, I, Part.Head, new THREE.BoxGeometry(0.065, 0.27, 0.25), M.cloth, 0, 1.45, -0.05, -0.15);
    }
  }
  {
    const cg = new THREE.CylinderGeometry(0.25, 0.37, 1.02, seg(16), 6, true, Math.PI * 0.66, Math.PI * 0.68), pp = cg.attributes.position;
    for (let i = 0; i < pp.count; i++) { const yy = pp.getY(i); if (yy < -0.3) pp.setY(i, yy + (hash(i, seed, 3) - 0.5) * 0.16); pp.setZ(i, pp.getZ(i) - (0.5 - yy) * 0.08); }
    cg.computeVertexNormals();
    P(sink, I, Part.Cloth, cg, cloth, 0, 0.64, -0.02);
  }
  P(sink, I, B, new THREE.TorusGeometry(0.215, 0.022, seg(6, 3), seg(24, 8)).rotateX(Math.PI / 2), Tr, 0, 0.67, 0, 0, 0, 0, 1, 1, 0.82);
  P(sink, I, B, new THREE.BoxGeometry(0.2, 0.3, 0.025), cloth, 0, 0.48, 0.2, 0.12);
  if(!boss) {
    P(sink, I, B, new THREE.BoxGeometry(0.16, 0.24, 0.018), cloth, 0, 0.94, 0.209, -0.12);
    P(sink, I, B, new THREE.BoxGeometry(0.02, 0.12, 0.021), Tr, 0, 0.95, 0.231, -0.12);
  }
  for (let i = 0; i < 5; i++) { const a = -0.9 + i * 0.45; P(sink, I, B, new THREE.SphereGeometry(0.014, seg(6), seg(4, 3)), Tr, Math.sin(a) * 0.25, 0.86, Math.cos(a) * 0.2); }
  ([-1, 1] as const).forEach((sd) => P(sink, I, B, new THREE.TorusGeometry(0.13, 0.012, seg(5, 3), seg(20, 8), Math.PI), Tr, sd * 0.3, 0.96, 0, 0, Math.PI / 2, sd * -0.35));

  if (boss) {
    ([-1, 1] as const).forEach((sd) => { for (let i = 0; i < 6; i++) { const t = i / 5, a = t * 2.1; P(sink, I, Part.Head, new THREE.ConeGeometry(0.055 * (1 - t * 0.75), 0.13, seg(8)), M.bossTrim, sd * (0.1 + Math.sin(a) * 0.22), 1.3 + t * 0.26 - Math.sin(a * 0.5) * 0.04, -0.03 - t * 0.06, -0.2, 0, -sd * (0.5 + a * 0.75)); } });
    for (let i = 0; i < 7; i++) { const a = (i - 3) * 0.3; P(sink, I, Part.Head, new THREE.ConeGeometry(0.016, 0.14, seg(5, Math.min(5, 4))), M.lava, Math.sin(a) * 0.1, 1.42, Math.cos(a) * 0.05, 0, 0, -a, 1, 1, 1, false); }
    for (let i = 0; i < 5; i++) P(sink, I, B, new THREE.BoxGeometry(0.014, 0.1 + hash(i, seed, 2) * 0.12, 0.012), M.lava, -0.14 + i * 0.07, 0.82 + (i % 2) * 0.06, 0.222, 0, 0, (hash(i, seed, 4) - 0.5) * 0.7, 1, 1, 1, false);
    ([-1, 1] as const).forEach((sd) => P(sink, I, B, new THREE.BoxGeometry(0.16, 0.012, 0.12), M.lava, sd * 0.3, 1.02, 0.02, 0, 0, sd * -0.35, 1, 1, 1, false));
    if (boss === 'brute') {
      P(sink, I, Part.ForearmR, new THREE.CylinderGeometry(0.02, 0.024, 1.9, seg(8)), M.leather, 0.36, 0.9, 0.16, 0.15, 0, 0.18);
      const ax = new THREE.Shape(); ax.moveTo(0, -0.2); ax.quadraticCurveTo(0.32, -0.26, 0.36, 0); ax.quadraticCurveTo(0.32, 0.26, 0, 0.2); ax.lineTo(0.04, 0); ax.closePath();
      ([-1, 1] as const).forEach((sd) => P(sink, I, Part.ForearmR, new THREE.ExtrudeGeometry(ax, { depth: 0.02, bevelEnabled: true, bevelThickness: 0.01, bevelSize: 0.01, bevelSegments: 1 }), M.bossTrim, 0.2, 1.72, 0.1, 0.15, sd > 0 ? 0 : Math.PI, 0.18, 1.1, 1.1, 1.1));
      P(sink, I, Part.ForearmR, new THREE.ConeGeometry(0.03, 0.2, seg(5, Math.min(5, 4))), M.bossTrim, 0.2, 1.95, 0.12, 0.15, 0, 0.18);
    } else {
      P(sink, I, B, new THREE.CylinderGeometry(0.018, 0.018, 2.2, seg(8)), M.iron, -0.05, 1.15, -0.24, -0.08);
      const fg = new THREE.PlaneGeometry(0.5, 0.8, 4, 8), fp = fg.attributes.position;
      for (let i = 0; i < fp.count; i++) fp.setZ(i, Math.sin(fp.getY(i) * 5 + fp.getX(i) * 3) * 0.03);
      fg.computeVertexNormals();
      P(sink, I, B, fg, M.bossCloth, -0.3, 1.8, -0.26, -0.08, Math.PI / 2 - 0.3, 0);
      P(sink, I, Part.ForearmR, new THREE.CylinderGeometry(0.016, 0.02, 1.6, seg(8)), M.iron, 0.38, 0.8, 0.12, 0.1);
      P(sink, I, Part.ForearmR, new THREE.IcosahedronGeometry(0.07, D < 0.7 ? 0 : 1), M.lava, 0.38, 1.66, 0.2, 0, 0, 0, 1, 1, 1, false);
    }
  }
}

/** Light raider: narrower armour, short blades, trailing hood and a cyan visor. */
function runner(sink: Sink, M: Materials) {
  const I = new THREE.Matrix4();
  for (const sd of [-1, 1]) {
    const upper = sd < 0 ? Part.LegL : Part.LegR, shin = sd < 0 ? Part.ShinL : Part.ShinR, foot = sd < 0 ? Part.FootL : Part.FootR;
    P(sink, I, upper, new THREE.CylinderGeometry(0.061, 0.05, 0.28, seg(8)), M.leather, sd * 0.09, 0.42, 0);
    P(sink, I, shin, new THREE.CylinderGeometry(0.055, 0.04, 0.25, seg(8)), M.iron, sd * 0.09, 0.16, 0);
    P(sink, I, foot, new THREE.BoxGeometry(0.1, 0.075, 0.2), M.iron, sd * 0.09, 0.04, 0.045);
    const arm = sd < 0 ? Part.ArmL : Part.ArmR;
    const forearm = sd < 0 ? Part.ForearmL : Part.ForearmR;
    P(sink, I, arm, new THREE.CylinderGeometry(0.048, 0.043, 0.21, seg(8)), M.leather, sd * 0.24, 0.90, 0.02, 0, 0, sd * 0.1);
    P(sink, I, forearm, new THREE.CylinderGeometry(0.043, 0.038, 0.22, seg(8)), M.leather, sd * 0.26, 0.68, 0.02, 0, 0, sd * 0.1);
    P(sink, I, forearm, new THREE.BoxGeometry(0.025, 0.3, 0.07), M.trim, sd * 0.28, 0.51, 0.09, -0.2);
    P(sink, I, forearm, new THREE.ConeGeometry(0.038, 0.14, seg(4, Math.min(4, 4))), M.trim, sd * 0.28, 0.36, 0.12, Math.PI - 0.2);
  }
  P(sink, I, Part.Body, new THREE.CylinderGeometry(0.14, 0.18, 0.44, seg(8)), M.leather, 0, 0.79, 0);
  P(sink, I, Part.Body, new THREE.SphereGeometry(0.19, seg(10), seg(8, 3)), M.iron, 0, 0.88, 0.025, 0, 0, 0, 1, 1.1, 0.6);
  P(sink, I, Part.Head, new THREE.ConeGeometry(0.14, 0.27, seg(8, Math.min(8, 4))), M.iron, 0, 1.22, -0.015, -0.12);
  P(sink, I, Part.Head, new THREE.BoxGeometry(0.17, 0.024, 0.028), M.eye, 0, 1.19, 0.095, 0, 0, 0, 1, 1, 1, false);
  P(sink, I, Part.Cloth, new THREE.PlaneGeometry(0.38, 0.68, 3, 5), M.clothDark, 0, 0.79, -0.16, 0.18);
}

/** Broad, horned pit demon with plated fists, a spiked mace and an exposed furnace. */
function pitboss(sink: Sink, M: Materials) {
  const I = new THREE.Matrix4(), B = Part.Body;
  for (const sd of [-1, 1]) {
    const leg = sd < 0 ? Part.LegL : Part.LegR, shin = sd < 0 ? Part.ShinL : Part.ShinR, foot = sd < 0 ? Part.FootL : Part.FootR;
    P(sink, I, leg, new THREE.CylinderGeometry(0.10, 0.085, 0.29, seg(8)), M.bossArmor, sd * 0.15, 0.415, 0);
    P(sink, I, shin, new THREE.CylinderGeometry(0.09, 0.075, 0.25, seg(8)), M.iron, sd * 0.15, 0.15, 0.02);
    P(sink, I, shin, new THREE.OctahedronGeometry(0.095), M.bossTrim, sd * 0.15, 0.28, 0.07);
    P(sink, I, foot, new THREE.BoxGeometry(0.18, 0.10, 0.28), M.iron, sd * 0.15, 0.05, 0.07);
    const arm = sd < 0 ? Part.ArmL : Part.ArmR, forearm = sd < 0 ? Part.ForearmL : Part.ForearmR;
    P(sink, I, B, new THREE.IcosahedronGeometry(0.22), M.bossArmor, sd * 0.36, 1.12, 0, 0, 0, sd * 0.25, 1.1, 0.8, 1);
    P(sink, I, arm, new THREE.CylinderGeometry(0.095, 0.08, 0.31, seg(8)), M.bossArmor, sd * 0.39, 0.965, 0);
    P(sink, I, forearm, new THREE.CylinderGeometry(0.08, 0.11, 0.28, seg(8)), M.iron, sd * 0.4, 0.66, 0.02);
    P(sink, I, forearm, new THREE.BoxGeometry(0.23, 0.19, 0.23), M.bossArmor, sd * 0.4, 0.51, 0.035);
    P(sink, I, forearm, new THREE.BoxGeometry(0.15, 0.024, 0.024), M.pitGlow, sd * 0.4, 0.52, 0.16, 0, 0, 0, 1, 1, 1, false);
    for (let i = 0; i < 3; i++) P(sink, I, B, new THREE.ConeGeometry(0.045, 0.21 + i * 0.04, seg(5, Math.min(5, 4))), M.bossTrim, sd * (0.3 + i * 0.07), 1.25, -0.08 + i * 0.08, 0, 0, -sd * 0.5);
    // Swept horns are a few tapered segments, merged with the other gold details.
    for (let i = 0; i < 4; i++) P(sink, I, Part.Head, new THREE.ConeGeometry(0.065 - i * 0.013, 0.16, seg(6, Math.min(6, 4))), M.bossTrim, sd * (0.12 + i * 0.065), 1.48 + i * 0.055, -0.02 - i * 0.04, -0.3 - i * 0.15, 0, -sd * (0.5 + i * 0.18));
  }
  P(sink, I, B, new THREE.CylinderGeometry(0.22, 0.27, 0.18, seg(10)), M.iron, 0, 0.62, 0);
  P(sink, I, B, new THREE.IcosahedronGeometry(0.38, D < 0.7 ? 0 : 1), M.bossArmor, 0, 0.96, 0, 0, 0, 0, 0.9, 1.05, 0.7);
  P(sink, I, B, new THREE.IcosahedronGeometry(0.085, D < 0.7 ? 0 : 1), M.pitGlow, 0, 0.94, 0.255, 0, 0, 0, 1, 1.2, 0.6, false);
  for (let i = 0; i < 5; i++) P(sink, I, B, new THREE.BoxGeometry(0.022, 0.09 + (i % 2) * 0.07, 0.018), M.pitGlow, (i - 2) * 0.06, 0.99, 0.235, 0, 0, (i - 2) * -0.2, 1, 1, 1, false);
  P(sink, I, Part.Head, new THREE.IcosahedronGeometry(0.17, D < 0.7 ? 0 : 1), M.iron, 0, 1.40, 0.025, 0, 0, 0, 0.9, 1, 0.8);
  P(sink, I, Part.Head, new THREE.BoxGeometry(0.22, 0.027, 0.04), M.pitGlow, 0, 1.42, 0.16, 0, 0, 0, 1, 1, 1, false);
  P(sink, I, Part.Head, new THREE.ConeGeometry(0.08, 0.16, seg(5, Math.min(5, 4))), M.bossTrim, 0, 1.29, 0.12, Math.PI);
  P(sink, I, Part.Cloth, new THREE.PlaneGeometry(0.45, 0.48, 3, 4), M.clothDark, 0, 0.46, 0.20, 0.08);
  P(sink, I, Part.ForearmR, new THREE.CylinderGeometry(0.035, 0.045, 0.95, seg(8)), M.iron, 0.44, 0.62, 0.18, 0.15);
  P(sink, I, Part.ForearmR, new THREE.IcosahedronGeometry(0.19), M.bossArmor, 0.44, 1.15, 0.26);
  for (let i = 0; i < 6; i++) {
    const a = i * Math.PI / 3;
    P(sink, I, Part.ForearmR, new THREE.ConeGeometry(0.038, 0.16, seg(5, Math.min(5, 4))), M.bossTrim, 0.44 + Math.cos(a) * 0.18, 1.15 + Math.sin(a) * 0.18, 0.26, 0, 0, a - Math.PI / 2);
  }
  P(sink, I, Part.ForearmR, new THREE.TorusGeometry(0.16, 0.012, seg(5, 3), seg(16, 8)), M.pitGlow, 0.44, 1.15, 0.27, 0, 0, 0, 1, 1, 1, false);
}

/** Kind codes in aSurf2.z, read by the horde shader. */
export const enum SurfaceKind { Plain = 0, Cloth = 1, BossArmor = 2 }

/** The three shared family materials (created once per Materials set). */
const familyCache = new WeakMap<Materials, { solid: THREE.MeshStandardMaterial; cloth: THREE.MeshStandardMaterial; glow: THREE.MeshBasicMaterial }>();
function families(M: Materials) {
  let f = familyCache.get(M);
  if (!f) familyCache.set(M, (f = {
    solid: new THREE.MeshStandardMaterial({ name: 'hordeSolid', color: 0xffffff, roughness: 1, metalness: 1 }),
    cloth: new THREE.MeshStandardMaterial({ name: 'hordeCloth', color: 0xffffff, roughness: 1, metalness: 1, side: THREE.DoubleSide }),
    glow: new THREE.MeshBasicMaterial({ name: 'hordeGlow', color: 0xffffff }),
  }));
  return f;
}
function familyOf(f: ReturnType<typeof families>, m: THREE.Material) {
  return (m as THREE.MeshBasicMaterial).isMeshBasicMaterial ? f.glow : m.side === THREE.DoubleSide ? f.cloth : f.solid;
}
/**
 * A source material's look as [colour.rgb, roughness] and [metalness, 0, kind, 0]. (Env intensity is not carried:
 * with scene.environment three.js uses scene.environmentIntensity for every standard material anyway.)
 */
function surface(m: THREE.Material): [number[], number[]] {
  const c = (m as THREE.MeshStandardMaterial).color;
  if ((m as THREE.MeshBasicMaterial).isMeshBasicMaterial) return [[c.r, c.g, c.b, 1], [0, 0, SurfaceKind.Plain, 0]];
  const s = m as THREE.MeshStandardMaterial;
  // plain soldier armour reads as worn iron rather than a mirror at the gameplay distance
  const armor = s.name === 'armor';
  const kind = s.name === 'cloth' || s.name === 'clothDark' ? SurfaceKind.Cloth : s.name === 'bossArmor' ? SurfaceKind.BossArmor : SurfaceKind.Plain;
  return [[c.r, c.g, c.b, armor ? 0.45 : s.roughness], [armor ? 0.65 : s.metalness, 0, kind, 0]];
}

export function buildWarrior(M: Materials, seed: number, style: 'brute' | 'herald' | 'runner' | 'pitboss' | null = null, detail = 1): WarriorTemplate {
  D = detail;
  const buckets = new Map<THREE.Material, { geos: THREE.BufferGeometry[]; cast: boolean }>();
  const sink: Sink = (geo, mat, m, part, cast = true) => {
    // Keep (or add) an index: shared vertices run the heavy pose shader once instead of once per triangle corner.
    const g = geo.clone();
    if (!g.index) g.setIndex(Array.from({ length: g.attributes.position.count }, (_, i) => i));
    g.clearGroups();
    g.applyMatrix4(m);
    for (const n of Object.keys(g.attributes)) if (n !== 'position' && n !== 'normal') g.deleteAttribute(n);
    g.setAttribute('aPart', new THREE.Float32BufferAttribute(new Float32Array(g.attributes.position.count).fill(part), 1));
    let b = buckets.get(mat);
    if (!b) buckets.set(mat, (b = { geos: [], cast }));
    b.geos.push(g);
  };
  if (style === 'runner') runner(sink, M); else if (style === 'pitboss') pitboss(sink, M); else soldier(sink, M, seed, style);
  // One draw per surface family instead of per material: each source material's look rides along
  // as vertex attributes (aSurf: linear colour + roughness, aSurf2: metalness, -, kind).
  const fam = families(M), groups = new Map<THREE.Material, { geos: THREE.BufferGeometry[]; cast: boolean }>();
  for (const [mat, b] of buckets) {
    const f = familyOf(fam, mat), look = surface(mat);
    for (const g of b.geos) {
      const n = g.attributes.position.count, a = new Float32Array(n * 4), a2 = new Float32Array(n * 4);
      for (let i = 0; i < n; i++) { a.set(look[0], i * 4); a2.set(look[1], i * 4); }
      g.setAttribute('aSurf', new THREE.Float32BufferAttribute(a, 4)); g.setAttribute('aSurf2', new THREE.Float32BufferAttribute(a2, 4));
    }
    let G = groups.get(f);
    if (!G) groups.set(f, (G = { geos: [], cast: false }));
    G.geos.push(...b.geos); G.cast ||= b.cast;
  }
  // a fixed family order keeps part indices stable across rebuilds (Horde.setDetail)
  const parts = [fam.solid, fam.cloth, fam.glow].filter(f => groups.has(f)).map((mat) => {
    const b = groups.get(mat)!, geo = mergeGeometries(b.geos)!;
    b.geos.forEach(g => g.dispose());
    return { mat, geo, cast: b.cast };
  });
  return style === 'pitboss'
    ? { parts, eye: new THREE.Vector3(0, 1.42, 0.16), core: new THREE.Vector3(0, 0.94, 0.255), height: 1.76 }
    : { parts, eye: new THREE.Vector3(0, 1.225, 0.13), core: new THREE.Vector3(0, 0.83, 0.24), height: 1.5 };
}
